import React, { FC, useEffect, useState } from "react";
import { ActionIcon, Button, Group, Image, Stack } from "@mantine/core";
import { Text } from "../Generics/Text";
import { IconTrash } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { ProjectDataType, StudyDataType } from "@/types/apiTypes";
import { useHomePageContext } from "@/hooks/contextHooks/useHomePageContext";
import { useFetchStudies } from "@/hooks/apiHooks/useFetchStudies";
import { useRemoveStudyFromProject } from "@/hooks/apiHooks/useRemoveStudyFromProject";
import { usePageTransition } from "@/hooks/helperHooks/usePageTransition";
import { vesselImageUrls } from "@/data/vesselImageUrls";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";
import styles from "../../css/gen.module.css";

type Props = {
  selectedProject: ProjectDataType | null;
  setDeleteStudyId: (id: string) => void;
  setIsDeleteStudyModalOpen: (val: boolean) => void;
  studyIsInProject: (
    study: StudyDataType,
    selectedProject: ProjectDataType,
  ) => boolean | undefined;
  handleStudyProjectAssociation: (study: StudyDataType) => void;
};

export const GenStudies: FC<Props> = ({
  selectedProject,
  setDeleteStudyId,
  setIsDeleteStudyModalOpen,
  studyIsInProject,
  handleStudyProjectAssociation,
}) => {
  // HOOK for Smooth transitions between pages
  const transition = usePageTransition();

  const navigate = useNavigate();
  const [studySearchTerm, setStudySearchTerm] = useState<string>("");

  const { colors, isDarkMode } = useAppColors();

  const { studies, setStudies } = useHomePageContext();

  const { data: studiesData, isLoading } = useFetchStudies(
    studySearchTerm,
    selectedProject?.id ?? null,
  );

  const {
    mutateAsync: removeStudyFromProject,
    // isSuccess: isSuccessRemovingStudyFromProject,
  } = useRemoveStudyFromProject();

  const [currentStudies, setCurrentStudies] =
    useState<StudyDataType[]>(studies);
  // console.log(studies);
  useEffect(() => {
    if (studySearchTerm) {
      setCurrentStudies(
        studies.filter((s) =>
          s.experiment_name
            ?.toLowerCase()
            .includes(studySearchTerm.toLowerCase()),
        ),
      );
    } else {
      setCurrentStudies(studies);
    }
  }, [studies, studySearchTerm]);

  useEffect(() => {
    if (studiesData) {
      setStudies(studiesData);
    }
  }, [studiesData, setStudies]);

  // useEffect(() => {
  //   invalidateStudies();
  // }, [selectedProject]);

  const handleStudyClick = (study: StudyDataType) => {
    navigate(`/study-details/${study.id}`);
  };
  const handleDeleteStudy = (study: StudyDataType) => {
    if (study.id) {
      setDeleteStudyId(study.id);
      setIsDeleteStudyModalOpen(true);
    }
  };

  const handleRemoveStudyFromProject = (study: StudyDataType) => {
    if (!selectedProject) {
      console.warn("No project selected, cannot remove study.");
      return;
    }

    removeStudyFromProject({ study, projectId: selectedProject.id })
      .then(() => console.log("Removed study from project."))
      .catch((err) => console.error(err));
  };

  if (isLoading || transition) return null;

  return (
    <div
      className={styles.container}
      style={{
        backgroundColor: colors.gray,
        borderColor: colors.border,
      }}
    >
      <div className={styles.boxHorizontal}>
        <h3 style={{ color: colors.seoncdary }} className={styles.title}>
          Studies
        </h3>

        <input
          placeholder="Search studies..."
          value={studySearchTerm}
          onChange={(e) => setStudySearchTerm(e.currentTarget.value)}
          className={styles.input}
        />
        <div
          style={{
            backgroundColor: isDarkMode ? colors.secondary : colors.brand,
          }}
          className={styles.btn}
          onClick={() =>
            navigate("/add-study", {
              state: { projectId: selectedProject?.id },
            })
          }
        >
          <Text size="small" weight="bold" color={colors.primary}>
            + Add new study
          </Text>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          marginTop: "1rem",
        }}
      >
        {currentStudies.length === 0 ? (
          <Text color={colors.secondary}>No studies found</Text>
        ) : (
          <div className={styles.cardContainer}>
            {currentStudies.map((study: any) => {
              const imageUrl =
                vesselImageUrls[study.vessel_type] ||
                "https://via.placeholder.com/300";

              return (
                <div key={study.id}>
                  <div
                    className={styles.card}
                    style={{
                      borderColor: colors.border,
                    }}
                  >
                    <div onClick={() => handleStudyClick(study)}>
                      <Image
                        src={imageUrl}
                        alt={study.vessel_type}
                        height={160}
                        style={{ borderRadius: 6 }}
                      />
                    </div>
                    <Group mt="md" mb="xs">
                      <Text color={colors.secondary}>
                        {study.experiment_name}
                      </Text>
                    </Group>
                    <Text color={colors.secondary} size="small">
                      Type: {study.vessel_type}
                    </Text>
                    <Text size="small" color={colors.secondary}>
                      Size: {study.vessel_size}
                    </Text>

                    <Stack mt="md">
                      {selectedProject &&
                      studyIsInProject(study, selectedProject) ? (
                        <Button
                          variant={"outline"}
                          color="black"
                          onClick={() => handleRemoveStudyFromProject(study)}
                          style={{
                            backgroundColor: isDarkMode
                              ? colors.secondary
                              : colors.red,
                            borderRadius: 5,
                            borderColor: colors.red,
                          }}
                        >
                          <Text
                            weight="bold"
                            size="small"
                            color={colors.primary}
                          >
                            Remove from Project
                          </Text>
                        </Button>
                      ) : (
                        <Button
                          variant={"outline"}
                          color="black"
                          onClick={() => handleStudyProjectAssociation(study)}
                          style={{
                            backgroundColor: isDarkMode
                              ? colors.secondary
                              : colors.brand,
                            borderRadius: 5,
                          }}
                        >
                          <Text
                            weight="bold"
                            size="small"
                            color={colors.primary}
                          >
                            Add to Project
                          </Text>
                        </Button>
                      )}
                      <Group justify="flex-end">
                        <ActionIcon
                          variant="subtle"
                          size="lg"
                          color={colors.red}
                          onClick={() => handleDeleteStudy(study)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Stack>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
